"use client";

import { KeyRound, LayoutDashboard, WalletCards } from "lucide-react";
import { usePathname } from "next/navigation";

import { Link } from "@/i18n/navigation";
import { cn } from "@/lib/utils";

const apiNavigation = [
  {
    title: "API Center",
    description: "Overview",
    href: "/api-center",
    icon: LayoutDashboard,
    isActive: (pathname: string) => pathname.endsWith("/api-center"),
  },
  {
    title: "API Keys",
    description: "Credentials",
    href: "/api-keys",
    icon: KeyRound,
    isActive: (pathname: string) => pathname.includes("/api-keys"),
  },
  {
    title: "API Billing",
    description: "Credits",
    href: "/api-billing",
    icon: WalletCards,
    isActive: (pathname: string) => pathname.endsWith("/api-billing"),
  },
] as const;

export default function ApiConsoleNav() {
  const pathname = usePathname() || "";

  return (
    <nav
      aria-label="Developer API"
      data-testid="api-console-nav"
      className="flex gap-2 overflow-x-auto rounded-[24px] border border-white/10 bg-white/[0.055] p-2.5 shadow-[0_20px_70px_rgba(0,0,0,0.34)] backdrop-blur-2xl lg:flex-col lg:overflow-visible"
    >
      {apiNavigation.map((item) => {
        const active = item.isActive(pathname);
        const ItemIcon = item.icon;

        return (
          <Link
            key={item.href}
            href={item.href}
            aria-current={active ? "page" : undefined}
            className={cn(
              "group flex min-w-[168px] items-center gap-3 rounded-[18px] border px-3.5 py-3 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-300/60 lg:min-w-0",
              active
                ? "border-sky-300/25 bg-sky-300/[0.1] text-white shadow-[0_14px_38px_rgba(14,165,233,0.1)]"
                : "border-transparent text-white/58 hover:border-white/10 hover:bg-white/[0.055] hover:text-white"
            )}
          >
            <span
              className={cn(
                "flex size-10 shrink-0 items-center justify-center rounded-2xl border transition",
                active
                  ? "border-sky-300/25 bg-sky-300/[0.1] text-sky-200"
                  : "border-white/10 bg-black/20 text-white/48 group-hover:text-white/78"
              )}
            >
              <ItemIcon className="size-4" />
            </span>
            <span className="min-w-0">
              <span className="block text-sm font-semibold">{item.title}</span>
              <span className="mt-0.5 block font-mono text-[9px] uppercase tracking-[0.18em] text-white/35">
                {item.description}
              </span>
            </span>
          </Link>
        );
      })}
    </nav>
  );
}
